import { useState } from 'react';
import { FloatingSelectField } from '../../components/FloatingSelectField';
import { InfoNote } from '../../components/InfoNote';
import { SectionHeader } from '../../components/SectionHeader';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { SubSelectCard } from '../../components/SubSelectCard';
import { ErrorIcon } from '../../icons/ErrorIcon';
import { Fields, InclusionList, Panel, NoticeSlot } from './PackageInclusionsPanel.styles';
import type { PackageInclusionsPanelProps } from './PackageInclusionsPanel.types';
import { radius } from '../../theme/tokens';

export function PackageInclusionsPanel({
  inclusions,
  onDayChange,
  onTimeChange,
  labels = {},
}: PackageInclusionsPanelProps) {
  const {
    title = 'Inclusions.',
    subtitle = 'Choose your preferred day and time for each.',
    dayLabel = 'Select day',
    timeLabel = 'Select time',
    noTimeslots = 'There are no time slots available.',
    noteDismissLabel,
  } = labels;

  const [dismissedNotes, setDismissedNotes] = useState<string[]>([]);

  const dismissNote = (inclusionId: string) =>
    setDismissedNotes((current) => [...current, inclusionId]);

  return (
    <Panel>
      <SectionHeader title={title} subtitle={subtitle} />
      <InclusionList>
        {inclusions.map((inclusion) => {
          const noTimes = !inclusion.isLoading && inclusion.timeOptions.length === 0;
          return (
            <SubSelectCard
              key={inclusion.id}
              heading={inclusion.title}
              subheading={inclusion.subtitle}
            >
              <Fields>
                <FloatingSelectField
                  label={dayLabel}
                  options={inclusion.dayOptions ?? []}
                  value={inclusion.selectedDay}
                  onChange={(value) => onDayChange(inclusion.id, value)}
                  error={inclusion.dayError}
                  fixedValueLabel={inclusion.fixedDayLabel}
                  aria-label={`${dayLabel} ${inclusion.title}`}
                />
                {inclusion.isLoading ? (
                  <SkeletonBlock height="72px" borderRadius={radius('card', 'radius-md')} />
                ) : (
                  <FloatingSelectField
                    label={timeLabel}
                    options={inclusion.timeOptions}
                    value={inclusion.selectedTime}
                    onChange={(value) => onTimeChange(inclusion.id, value)}
                    error={noTimes ? undefined : inclusion.timeError}
                    disabled={noTimes}
                    aria-label={`${timeLabel} ${inclusion.title}`}
                  />
                )}
              </Fields>
              {noTimes && (
                <NoticeSlot
                  data-error={inclusion.timeError ? 'true' : undefined}
                  aria-invalid={inclusion.timeError ? true : undefined}
                  tabIndex={inclusion.timeError ? -1 : undefined}
                >
                  <InfoNote
                    variant={inclusion.timeError ? 'danger' : 'warning'}
                    icon={<ErrorIcon size={19} />}
                    text={noTimeslots}
                  />
                </NoticeSlot>
              )}
              {!noTimes && inclusion.noteText && !dismissedNotes.includes(inclusion.id) && (
                <NoticeSlot>
                  <InfoNote
                    text={inclusion.noteText}
                    onDismiss={() => dismissNote(inclusion.id)}
                    dismissLabel={noteDismissLabel}
                  />
                </NoticeSlot>
              )}
            </SubSelectCard>
          );
        })}
      </InclusionList>
    </Panel>
  );
}
